import * as Bacon from 'baconjs';
import {
    CDN_URL,
    COMPARE_AT,
    DISCOUNTED,
    ONETIME,
    ORIGINAL,
    STEP_LIMIT,
    SUBSCRIPTION,
    SUBSCRIPTION_ONLY,
    VARIANT_OPTIONS,
    WIDGET_DEFAULTS
} from './constants.js';
import DefaultPreset from './presets/_default';
import Emitters from './emitters.js';
import Pricing from './pricing.js';
import Product from './product.js';
import WidgetHTML from './html/default.html';
import { isString } from 'lodash';
import presets from './presets';

class Widget {
    constructor({
        product,
        selectors,
        props,
        debug,
        events,
        classes,
        widget,
        preset,
        productId,
        injectParent,
        injectMethod
    }) {
        if (!productId) return;

        alert('hello world');

        // Adds instantiated widget to instances array
        Widget.instances.push(this);
        window.RcWidgets = Widget.instances;

        this.debug = debug;

        // `preset` can be a string or a class
        // when string, look for existing preset, otherwise assume user has customized preset
        // fallback to DefaultPreset when needed
        const Preset = isString(preset)
            ? presets[preset]
            : preset || DefaultPreset;

        this.preset = new Preset({
            debug,
            INJECTION_PARENT: injectParent,
            injectMethod
        });

        this.selectors = {
            addToCart: `[name='add'], [type="submit"]`,
            widgetContainer: '[data-widget-container]',
            widget: '[data-widget]',
            radioOnetime: '[data-radio-onetime]',
            radioSubsave: '[data-radio-subsave]',
            priceOnetime: '[data-price-onetime]',
            priceSubsave: '[data-price-subsave]',
            optionOnetime: '[data-option-onetime]',
            optionSubsave: '[data-option-subsave]',
            selectorOnetime: '[data-selector-onetime]',
            selectorSubsave: '[data-selector-subsave]',
            labelOnetime: '[data-label-onetime]',
            labelSubsave: '[data-label-subsave]',
            labelTextOnetime: '[data-label-text-onetime]',
            labelTextSubsave: '[data-label-text-subsave]',
            labelDiscount: '[data-label-discount]',
            plansContainer: '[data-plans-container]',
            plansDropdown: '[data-plans-dropdown]',
            variantOptions: VARIANT_OPTIONS.join(','),
            priceString: 'price',
            variantTrigger: null,
            globalEventTriggers: 'variant:changed',
            price: null,
            ...selectors
        };
        this.classes = {
            optionActive: 'rc_widget__option--active',
            ...classes
        };
        this.props = {
            stepLimiter: 4 || STEP_LIMIT,
            priceAttribute: 'data-price-mutation-watcher',
            throttle: 0,
            active: true, // It's saved as a metafield, but not included in widget settings yet
            moneyFormat: window.theme?.moneyFormat || '${{amount}}', // This is tricky to get, not all themes provide it
            currency: window.Shopify?.currency?.active || 'USD', // We should get from Store table
            ...props
        };

        this.productId = productId;
        this.productData = product;
        this.widgetData = {
            ...WIDGET_DEFAULTS,
            ...widget
        };
        this.container = {};

        this.events = events || {};
        this.state = {};
        this.product = {};
        this.pricing = {};
        this.emitters = {};
    }

    async build() {
        const data = await this.getProductData();
        this.productData = this.filterProductDataById(
            data || this.productData,
            Number(this.productId)
        );
        this.widgetData = {
            ...this.widgetData,
            ...data.widget_settings
        };

        if (!this.productData) return;

        const widget = this.generateWidget();
        this.preset._safeInject(widget);

        this.container = this.findContainer();

        if (
            !this.container?.querySelectorAll(this.selectors.variantOptions)
                .length
        ) {
            window.console.warn(
                'ReCharge: Variant options not found in container'
            );
            window.console.log(
                `this.selectors.variantOptions: ${this.selectors.variantOptions}`
            );
        }

        this.product = new Product({
            data: this.productData,
            selectors: this.selectors,
            props: this.props,
            container: this.container
        });

        this.pricing = new Pricing({
            selectors: this.selectors,
            props: this.props,
            classes: this.classes,
            container: this.container
        });
        this.emitters = new Emitters({
            product: this.product,
            pricing: this.pricing,
            selectors: this.selectors,
            props: this.props,
            classes: this.classes,
            container: this.container
        });

        this.setupStreams();
        this.setupEventEmitters();
        this.show();
    }

    // an array of all instances
    static instances = [];

    static getAllWidgets() {
        return Widget.instances;
    }

    static getWidgetsByProductId(productId) {
        return Widget.getAllWidgets().filter(
            widget => widget.productId === productId
        );
    }

    DISCOUNTED = DISCOUNTED;
    ORIGINAL = ORIGINAL;
    COMPARE_AT = COMPARE_AT;
    SUBSCRIPTION = SUBSCRIPTION;
    ONETIME = ONETIME;
    STEP_LIMIT = STEP_LIMIT;

    get enablePriceWatcher() {
        return this.pricing.priceContainer.hasAttribute(
            this.props.priceAttribute
        );
    }

    generateWidget() {
        /*
            Create widget HTML
            - Perform label injections for translations
            - Modify UI elements based on subscription settings
            - Build the selling plan selector
        */
        const template = WidgetHTML;
        const widget = document.createElement('div');
        widget.innerHTML = template;

        // Set radio input name attribute
        const sellingPlanGroupId = this.productData.selling_plan_groups[0]
            ?.selling_plan_group_id;
        const radioName = `selling_plan_${sellingPlanGroupId}`;
        widget.querySelector(this.selectors.radioSubsave).name = radioName;
        widget.querySelector(this.selectors.radioOnetime).name = radioName;

        // Set Onetime input and label reference
        const onetimeId = `product_${this.productId}_group_${sellingPlanGroupId}_onetime`;
        widget.querySelector(this.selectors.radioOnetime).id = onetimeId;
        widget
            .querySelector(this.selectors.labelOnetime)
            .setAttribute('for', onetimeId);

        // Set Subsave input and label reference
        const subsaveId = `product_${this.productId}_group_${sellingPlanGroupId}_subsave`;
        widget.querySelector(this.selectors.radioSubsave).id = subsaveId;
        widget
            .querySelector(this.selectors.labelSubsave)
            .setAttribute('for', subsaveId);

        // Set purchase type labels
        widget.querySelector(
            this.selectors.labelTextOnetime
        ).innerHTML = `${this.widgetData.onetime_message}:`;
        widget.querySelector(
            this.selectors.labelTextSubsave
        ).innerHTML = `${this.widgetData.subscribe_message}:`;

        // Set discount label
        widget.querySelector(
            this.selectors.labelDiscount
        ).innerHTML = `${this.productData.selling_plan_groups[0]?.selling_plans[0]?.price_adjustments_value}%`;

        // Set subscription-only changes
        if (
            this.productData.subscription_options
                ?.storefront_purchase_options === SUBSCRIPTION_ONLY
        ) {
            widget.querySelector(this.selectors.optionOnetime).remove();
            widget.querySelector(this.selectors.selectorSubsave).remove();
        }

        // Build selling plans
        const dropdown = widget.querySelector(this.selectors.plansDropdown);
        this.productData.selling_plan_groups[0].selling_plans.forEach(plan => {
            const option = document.createElement('option');
            option.innerHTML = plan.selling_plan_name;
            option.value = plan.selling_plan_id;
            dropdown.append(option);
        });

        return widget;
    }

    findContainer() {
        /*
            Locate the product preset injection point. If variant options
            aren't found within the product form, we'll step through the parent 
            elements until they're located.
        */
        const productForm = this.preset.injectionParent;

        if (!productForm) {
            window.console.warn('ReCharge: Product form not found');
            window.console.log(
                `this.selectors.productForm: ${this.selectors.productForm}`
            );
        }

        if (
            productForm.querySelectorAll(this.selectors.variantOptions).length
        ) {
            return productForm;
        }

        let container = productForm;
        let options = [];
        let step = 0;

        do {
            options =
                container?.querySelectorAll(this.selectors.variantOptions) ||
                [];
            container = container?.parentElement;
            step++;
        } while (!options.length && step <= this.STEP_LIMIT);

        return options.length ? container : productForm;
    }

    setState(newState) {
        this.state = { ...this.state, ...newState };
    }

    emit(eventName, eventValue) {
        const handleEvent = this.events[eventName];
        if (handleEvent) handleEvent(eventValue);
        if (this.debug)
            window.console.log(
                '[event]' + eventName + ' : ' + JSON.stringify(eventValue)
            );
    }

    setPriceWatcher(enable) {
        if (enable) {
            this.pricing.priceContainer.setAttribute(
                this.props.priceAttribute,
                ''
            );
        } else {
            this.pricing.priceContainer.removeAttribute(
                this.props.priceAttribute
            );
        }
    }

    setupStreams() {
        const $priceContainer = Bacon.mergeAll([
            Bacon.once(() => {
                this.setPriceWatcher(true);
            }),
            Bacon.fromBinder(callback => {
                this.priceContainerObserver(
                    callback,
                    this.pricing.priceContainer
                );
            })
        ]);
        const $productPrice = $priceContainer.combine(
            this.emitters.allPrices,
            (mutation, priceData) => ({
                hasDiscount: priceData.hasDiscount,
                priceOnetime: priceData.priceOnetime,
                priceSubsave: priceData.priceSubsave,
                subscriptionActive: priceData.subscriptionActive
            })
        );

        this.streams = {
            subscriptionActive: this.emitters.subscriptionActive,
            purchaseType: this.emitters.purchaseType,
            currentVariantId: this.emitters.currentVariantId,
            sellingPlanId: this.emitters.sellingPlanId,
            addToCart: this.emitters.addToCart,
            priceOnetime: this.emitters.priceOnetime,
            priceSubsave: this.emitters.priceSubsave,
            allPrices: this.emitters.allPrices,
            priceContainer: $priceContainer,
            productPrice:
                // Use throttle to resolve race condition issues on themes
                this.props.throttle !== null
                    ? $productPrice.throttle(this.props.throttle)
                    : $productPrice
        };

        Object.entries(this.streams).forEach(([streamName, stream]) => {
            stream &&
                stream.onValue(val => {
                    this.setState({ [streamName]: val });
                    if (this.debug)
                        window.console.log(
                            '[state]' + streamName + ' : ' + val
                        );
                });
        });
    }

    setupEventEmitters() {
        this.streams.subscriptionActive?.onValue(visible => {
            // Triggers when the subscription radio option changes
            this.subscriptionActiveChange(visible);
            this.emit('subscriptionActiveChange', visible);
        });

        this.streams.purchaseType?.onValue(selected => {
            // Triggers when the purchase type changes
            this.emit('onPurchaseTypeChange', selected);
        });

        this.streams.currentVariantId?.onValue(variantId => {
            // Triggers when the variant ID changes
            this.emit('onVariantChange', variantId);
        });

        this.streams.sellingPlanId?.onValue(sellingPlanId => {
            // Triggers when the selling plan ID changes
            this.emit('onSellingPlanChange', sellingPlanId);
        });

        this.streams.priceOnetime?.onValue(price => {
            // Triggers when the regular price changes
            this.emit('onPriceRegularChange', price);
        });

        this.streams.priceSubsave?.onValue(price => {
            // Triggers when the discounted price changes
            this.emit('onPriceSaleChange', price);
        });

        this.streams.allPrices?.onValue(prices => {
            // When any any variant price change occurs
            this.onAllPricesChange(prices);
            this.emit('onAnyPriceChange', prices);
        });

        this.streams.productPrice?.onValue(priceData => {
            // Triggers when the product price changes
            this.onProductPriceChange(priceData);
            this.emit('onProductPriceChange', priceData);
            if (this.props.priceTemplates?.container) {
                this.emit('onPriceTemplateChange', priceData);
            }
        });

        this.streams.priceContainer?.onValue(priceData => {
            // Triggers when the price container detects a mutation
            this.emit('onPriceContainerChange', priceData);
        });

        this.streams.addToCart?.onValue(formData => {
            // Triggers when the addToCart button is clicked
            this.emit('onAddToCart', formData);
        });
    }

    subscriptionActiveChange = visible => {
        // Conditionally display the subscription plan dropdown container
        const planContainer = this.container.querySelector(
            this.selectors.plansContainer
        );
        if (planContainer)
            planContainer.style.display = visible ? 'block' : 'none';

        const planDropdown = this.container.querySelector(
            this.selectors.plansDropdown
        );
        if (planDropdown) planDropdown.name = visible ? 'selling_plan' : '';

        if (this.product.subscriptionOnly) return;

        const onetimeOption = this.container.querySelector(
            this.selectors.optionOnetime
        );
        const subsaveOption = this.container.querySelector(
            this.selectors.optionSubsave
        );

        if (this.product.subscriptionOnly) return;

        // Add active class to options if subscriptions are optional
        if (visible) {
            onetimeOption.classList.remove(this.classes.optionActive);
            subsaveOption.classList.add(this.classes.optionActive);
        } else {
            onetimeOption.classList.add(this.classes.optionActive);
            subsaveOption.classList.remove(this.classes.optionActive);
        }
    };

    onAllPricesChange = priceData => {
        // Update inline radio price labels
        if (!priceData.hasDiscount) return;
        this.container
            .querySelectorAll(this.selectors.priceOnetime)
            .forEach(elem => (elem.innerHTML = priceData.priceOnetime));
        this.container
            .querySelectorAll(this.selectors.priceSubsave)
            .forEach(elem => (elem.innerHTML = priceData.priceSubsave));
    };

    priceUpdateWithTemplate = (priceData, priceTemplates) => {
        // Use price templates if they've been provided
        const template = priceData.subscriptionActive
            ? priceTemplates.subsave(
                  priceData.priceSubsave,
                  priceData.priceOnetime
              )
            : priceTemplates.onetime(priceData.priceOnetime);
        const priceContainer = document.querySelectorAll(
            priceTemplates.container
        );
        priceContainer.forEach(elem => {
            elem.innerHTML = template;
        });
    };

    priceUpdateWithSearch = (priceData, priceElements) => {
        priceElements.forEach(elem => {
            elem.innerHTML = priceData.subscriptionActive
                ? priceData.priceSubsave
                : priceData.priceOnetime;
        });
    };

    priceContainerObserver = (callback, element) => {
        // Price element DOM mutation event
        if (!element) return;
        var observer = new MutationObserver(mutations =>
            mutations.forEach(() => {
                if (this.enablePriceWatcher) {
                    callback();
                }
            })
        );
        observer.observe(element, {
            subtree: true,
            childList: true
        });
    };

    onProductPriceChange = priceData => {
        if (!this.pricing.priceContainer) return;
        // Trigger the product price changes
        // Disable price watcher while making changes
        this.setPriceWatcher(false);
        // Rebuild selectors if theme removes references
        this.pricing.rebuildPrices();
        if (
            this.props.priceTemplates?.subsave &&
            this.props.priceTemplates?.onetime
        ) {
            // Use price template replacement if provided
            this.priceUpdateWithTemplate(priceData, this.props.priceTemplates);
        } else {
            // Fallback to automatic price search and replacement
            this.priceUpdateWithSearch(priceData, this.pricing.mainPrices);
        }
        setTimeout(() => {
            // Reset price watcher
            this.setPriceWatcher(true);
        }, 20);
    };

    modifyBackButtonBehavior() {
        // Reload page when hitting back button
        window.addEventListener(
            'pageshow',
            function (event) {
                if (
                    event.persisted ||
                    (window.performance &&
                        window.performance.navigation.type === 2)
                ) {
                    window.location.reload();
                }
            },
            false
        );
    }

    unhideWidget() {
        // We could potentially move this to the CSS, since it's embedded using JS
        const widgetContainer = this.container.querySelector(
            this.selectors.widgetContainer
        );
        widgetContainer.style.display = 'block';
    }

    show() {
        this.modifyBackButtonBehavior();
        this.unhideWidget();
    }

    get shopUrl() {
        /*
            Returns the Shopify URL
            - Depends on the Shopify global object, found on all Shopify stores
        */
        return window?.Shopify?.shop;
    }

    get productDataUrl() {
        // Build the URL to fetch product data from
        return `${CDN_URL}/store/${this.shopUrl}/product/2020-12/${this.productId}.json`;
    }

    getProductData() {
        try {
            // Fetch and return product data JSON using the product data URL
            return fetch(this.productDataUrl).then(resp => resp.json());
        } catch (e) {
            window.console.warn('ReCharge: Product data not found');
            window.console.log({
                productDataUrl: this.productDataUrl,
                error: e
            });
            return {};
        }
    }

    filterProductDataById(data = {}, id) {
        // Filter and return product data by provided ID
        return this.products?.length
            ? data.products?.find(product => product.external_product_id === id)
            : data.product.external_product_id === id && data.product;
    }
}

export default Widget;
